import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PromptService } from '../promtp-service/prompt.service';
import { OrganizationApiConfiguration } from 'src/app/api/proxy/organization/organization-api-configuration';
import { MPIApiConfiguration } from 'src/app/api/proxy/mpi/mpi-api-configuration';
import {
  consumerGetByTokenTokenGet,
  consumerGetByTokenPartOfOrganizationIdTokenGet,
  consumerMyOrganizationGet,
  consumerGetByIdOrganizationIdGet
} from 'src/app/api/proxy/organization/functions';
import { consumerGet, consumerOrganizationConsentIdPost } from 'src/app/api/proxy/mpi/functions';
import { OrganizationCodeModel, OrganizationTokenModel } from 'src/app/api/proxy/organization/organization-models';
import { firstValueFrom, timeout } from 'rxjs';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { ConsentUpdateModel, PatientModel } from 'src/app/api/proxy/mpi/mpi-models';
import { EventService } from '../events/event.service';
import { PointsService } from '../points-service/points.service';
import { UserInfoService } from '../auth-service/user-info-service';
import { IAppEnvironment } from 'src/app/api/proxy/app-environment/app-environment-models';
import { StorageKey } from '../storage/models/storage-key.enum';
import { LoadingService } from '../loading-service/loading.service';
import { Network } from '@capacitor/network';
import { AuthService } from '../auth-service/auth.service';
import { BaseApiService } from '../base-api-service/base-api.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService extends BaseApiService {
  private NETWORK_TIMEOUT = 25000;
  private DISABLED_TIME_SECOND = 60;
  private INPUT_INVALID_ATTEMPTS = 10;
  private attempts = 0;
  private disabledSeconds = 0;

  private envJson?: string;

  constructor(
    protected _PromptService: PromptService,
    private http: HttpClient,
    private organizationCfg: OrganizationApiConfiguration,
    private mpiCfg: MPIApiConfiguration,
    private _pointsService: PointsService,
    private _accountService: AuthService
  ) {
    super(_PromptService);

    EventService.environmentLoaded.subscribe((env: IAppEnvironment) => {
      let jsn = JSON.stringify(env);
      if (jsn != this.envJson) {
        this.envJson = jsn;
        this.nextOrgRefresh = null;
      }
    });
  }

  public GetByTokenDisabledSeconds(): number {
    return this.disabledSeconds;
  }

  public async GetByTokenAsync(organizationToken: string): Promise<OrganizationTokenModel | null> {
    if (this.disabledSeconds > 0)
      return null;

    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }


    try {
      const obs = environment.organizationId
        ? consumerGetByTokenPartOfOrganizationIdTokenGet(this.http, this.organizationCfg.rootUrl, {
            partOfOrganizationId: environment.organizationId,
            token: organizationToken,
          })
        : consumerGetByTokenTokenGet(this.http, this.organizationCfg.rootUrl, { token: organizationToken });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;

      // Flavour parent lock (e.g. patientnavigator): only accept the parent org itself
      // or a child whose partOfOrganizationId matches environment.organizationId.
      if (!this.isOrganizationUnderFlavourParent(response)) {
        this.registerInvalidTokenAttempt();
        return null;
      }

      this.attempts = 0;
      this.disabledSeconds = 0;
      return response;
    } catch (error) {

      this.registerInvalidTokenAttempt();
      return null;
    }
  }

  /**
   * When environment.organizationId is set, org codes must resolve to that org
   * or a direct child (partOfOrganizationId === environment.organizationId).
   */
  public isOrganizationUnderFlavourParent(
    org: Pick<OrganizationTokenModel, 'id' | 'partOfOrganizationId'> | null | undefined,
  ): boolean {
    const parentId = environment.organizationId?.trim();
    if (!parentId) {
      return true;
    }
    if (!org) {
      return false;
    }
    const parentNorm = parentId.toLowerCase();
    const orgId = org.id?.trim().toLowerCase();
    const partOf = org.partOfOrganizationId?.trim().toLowerCase();
    return orgId === parentNorm || partOf === parentNorm;
  }

  private registerInvalidTokenAttempt(): void {
    this.attempts++;
    if (this.attempts > this.INPUT_INVALID_ATTEMPTS) {
      this.disabledSeconds = this.DISABLED_TIME_SECOND;
      setTimeout(() => {
        this.checkDisabledSeconds();
      }, 1000);
    }
  }

  private checkDisabledSeconds() {
    this.disabledSeconds--;
    if (this.disabledSeconds <= 0) {
      this.attempts = 0;
      this.disabledSeconds = 0;
    } else {
      setTimeout(() => {
        this.checkDisabledSeconds();
      }, 1000);
    }
  }

  public async SetDefaultOrganization(defaultOrg: OrganizationCodeModel): Promise<void> {
    await SecureStorageService.set(SecureStorageKey.DefualtOrginization, JSON.stringify(defaultOrg));
    EventService.organizationConfigLoaded.emit(defaultOrg as OrganizationTokenModel);
  }

  public async GetDefaultOrganization(): Promise<OrganizationCodeModel | null> {
    const defaultOrgStr = await SecureStorageService.get(SecureStorageKey.DefualtOrginization);
    if (!defaultOrgStr) {
      return null;
    }
    return JSON.parse(defaultOrgStr) as OrganizationCodeModel;
  }

  public async SubscriptionRequired(): Promise<boolean | null> {
    let defaultOrg = await this.GetDefaultOrganization();
    if (!defaultOrg)
      return null;

    if (defaultOrg.subscriptionRequired == undefined) {
      return null;
    }
    return defaultOrg.subscriptionRequired;
  }

  public async SkinConditionEnabled(): Promise<boolean | null> {
    const defaultOrg = await this.GetDefaultOrganization();
    if (!defaultOrg) {
      return null;
    }

    if (defaultOrg.skinConditionEnabled === undefined) {
      return null;
    }

    return defaultOrg.skinConditionEnabled;
  }

  public async GrantConsentForOrganization(organizationId: string, granted: boolean = true): Promise<boolean> {
    const request: ConsentUpdateModel = {
      granted
    };
    const obs = consumerOrganizationConsentIdPost(this.http, this.mpiCfg.rootUrl, { id: organizationId, body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'GrantConsentForOrganization');
    const response = httpResponse ? httpResponse.body : null;

    if (response === null) {
      return false;
    }

    // Update the cache
    const obs2 = consumerGet(this.http, this.mpiCfg.rootUrl);
    const httpResponse2 = await this.callApi(obs2, this.NETWORK_TIMEOUT, 'GrantConsentForOrganization-GetPatient');
    const patient = httpResponse2 ? httpResponse2.body : null;

    if (patient) {
      (patient as any).fetchedAt = new Date().toISOString();
      await SecureStorageService.set(StorageKey.PatientModel, JSON.stringify(patient));
    }

    return response;
  }

  public async loadOrganizationStatic(): Promise<OrganizationCodeModel | null> {
    let result = await this.GetDefaultOrganization();
    if (result) {
      EventService.organizationConfigLoaded.emit(result);
    }
    return result;
  }

  /** Allows the next loadOrganization call to bypass the in-memory refresh schedule. */
  public invalidateScheduledRefresh(): void {
    this.nextOrgRefresh = null;
  }

  private nextOrgRefresh: Date | null = null;
  /** Shared in-flight load so concurrent callers await the same fetch instead of no-oping. */
  private loadOrgPromise: Promise<void> | null = null;

  public async loadOrganization(source: string, forced: boolean = false): Promise<void> {
    if (this.loadOrgPromise) {
      if (!forced) {
        return this.loadOrgPromise;
      }
      try {
        await this.loadOrgPromise;
      } catch {
        // Previous load failed; continue with forced refresh.
      }
    }

    this.loadOrgPromise = this.executeLoadOrganization(source, forced).finally(() => {
      this.loadOrgPromise = null;
    });
    return this.loadOrgPromise;
  }

  private async executeLoadOrganization(source: string, forced: boolean): Promise<void> {
    let silentLoad = false;

    if (forced) {
      this.nextOrgRefresh = null;
    }

    const now = new Date();
    const cachedOrg = await this.GetDefaultOrganization();
    if (cachedOrg) {
      EventService.organizationConfigLoaded.emit(cachedOrg);
      silentLoad = true;
    }

    if (this.nextOrgRefresh != null && this.nextOrgRefresh > now) {
      if (cachedOrg) {
        return;
      }
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return;
    }

    this.nextOrgRefresh = new Date(now.getTime() + 60 * 60000); // 60 minutes in milliseconds
    try {
      if (silentLoad) {
        LoadingService.addSilentUrl(consumerMyOrganizationGet.PATH);
      }
      const obs = consumerMyOrganizationGet(this.http, this.organizationCfg.rootUrl);
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const org = httpResponse.body!;
      if (silentLoad) {
        LoadingService.removeSilentUrl(consumerMyOrganizationGet.PATH);
      }

      if (!(environment.apiProduction ?? environment.production)) {
        org.aiChatEnabled = true;
      }

      if (!org.vivaScorePointsModuleEnabled) {
        const myPoints = await this._pointsService.currentBalance(true);
        if (myPoints && myPoints.currentPoints) {
          org.vivaScorePointsModuleEnabled = true;
        }
      }

      await this.SetDefaultOrganization(org);
      EventService.organizationConfigLoaded.emit(org);
    } catch (error) {
      if (silentLoad) {
        LoadingService.removeSilentUrl(consumerMyOrganizationGet.PATH);
      }
      const org = await this.GetDefaultOrganization();
      if (org) {
        EventService.organizationConfigLoaded.emit(org);
      }
    }
  }

  /**
   * Ensures organization branding (logo) is available for registration pages.
   * Pre-login: refreshes via GetById when cache is missing logoBase64.
   */
  public async ensureOrganizationBranding(): Promise<OrganizationTokenModel | null> {
    const org = this.asOrganizationWithBranding(await this.GetDefaultOrganization());
    if (org?.logoBase64) {
      return org;
    }

    return this.refreshOrganizationFromApi(org);
  }

  /**
   * Ensures organization (including consent HTML) is available.
   * Pre-login registration refreshes via GetById; logged-in users use MyOrganization.
   */
  public async ensureOrganizationForConsent(): Promise<OrganizationCodeModel | null> {
    let org = this.asOrganizationWithBranding(await this.GetDefaultOrganization());
    if (org?.vivaScoreConsent) {
      return org;
    }

    if (org?.id) {
      org = (await this.refreshOrganizationFromApi(org)) ?? org;
    }
    if (org?.vivaScoreConsent) {
      return org;
    }

    const localUser = await UserInfoService.getUserInfo();
    if (!localUser) {
      return org;
    }

    await this.loadOrganization('ensureOrganizationForConsent', true);
    org = this.asOrganizationWithBranding(await this.GetDefaultOrganization());
    if (org?.vivaScoreConsent) {
      return org;
    }

    await this.loadOrganization('ensureOrganizationForConsent-retry', true);
    return this.GetDefaultOrganization();
  }

  /** Cached org JSON may include token fields (e.g. logoBase64) not on OrganizationCodeModel. */
  private asOrganizationWithBranding(
    org: OrganizationCodeModel | null
  ): OrganizationTokenModel | null {
    return org as OrganizationTokenModel | null;
  }

  private async refreshOrganizationFromApi(
    org: OrganizationTokenModel | null
  ): Promise<OrganizationTokenModel | null> {
    const orgId = org?.id ?? environment.organizationId;
    if (!orgId) {
      return org;
    }

    const refreshed = await this.GetByIdAsync(orgId);
    if (refreshed) {
      await this.SetDefaultOrganization(refreshed);
      EventService.organizationConfigLoaded.emit(refreshed);
      return refreshed;
    }

    return org;
  }

  public async GetByIdAsync(organizationId: string): Promise<OrganizationTokenModel | null> {
    const obs = consumerGetByIdOrganizationIdGet(this.http, this.organizationCfg.rootUrl, { organizationId: organizationId });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'GetByIdAsync');
    return httpResponse ? httpResponse.body : null;
  }

  public async PersonConscentGranted(forced: boolean = false): Promise<boolean> {
    const myPatient = await SecureStorageService.get(StorageKey.PatientModel);
    let model: PatientModel | null = null;
    let cacheTimestamp: Date | null = null;

    if (myPatient) {
      try {
        model = JSON.parse(myPatient);
      } catch {
        model = null
      }
    }

    if (model) {
      const ts = (model as any).fetchedAt;
      if (ts) {
        cacheTimestamp = new Date(ts);
      }
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Go back 1 day
    yesterday.setHours(23, 59, 59); // Set to 23:59:59.999

    if (model && cacheTimestamp && !forced) {
      const now = new Date();
      const ageHours = (now.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

      const cacheDay = cacheTimestamp.getDate();

      const nowDay = now.getDate();

      const isSameDay = cacheDay === nowDay;

      if (isSameDay && model.vivaScoreConsentGranted) {
        return true;
      }
    }

    // Use custom network check and error handling to preserve behavior:
    // - Return false if network is disconnected
    // - Return true (not prompt) on network errors
    const status = await Network.getStatus();
    if (!status.connected) {
      return false;
    }

    try {
      const obs = consumerGet(this.http, this.mpiCfg.rootUrl);
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      const response = httpResponse.body!;

      if (response) {
        (response as any).fetchedAt = new Date().toISOString();
        await SecureStorageService.set(StorageKey.PatientModel, JSON.stringify(response));
        return response.vivaScoreConsentGranted == true;
      }

    } catch (error) {
      return true; // lets not prompt on network issues
    }
    return false;
  }

  public async GrantPatientConscent(): Promise<boolean> {

    const myPatient = await SecureStorageService.get(StorageKey.PatientModel);
    let model: PatientModel | null = null;
    if (myPatient) {
      try {
        model = JSON.parse(myPatient);
      } catch {
        model = null
      }
    }

    let orgId: string | null = null;

    if (model == null || !model.organizationId) {
      var defaultOrg = await this.GetDefaultOrganization();
      if (defaultOrg)
        orgId = defaultOrg.id!;
    } else {
      orgId = model.organizationId;
    }

    if (orgId) {
      return await this.GrantConsentForOrganization(orgId);
    }

    await this._accountService.clearUserData(true);
    await this._accountService.logout('GrantPatientConscent');

    return false;

  }

}
